import { Link } from "react-router-dom";
import {
  ArrowRight,
  HeartPulse,
  Activity,
  Users,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { RevealHeading, Reveal } from "../components/motion";
import { Programs } from "../components/Programs";
import { COURSES, EMAIL, PHONE, PHONE_DISPLAY, JOTFORM_URL, SERVICE_AREA } from "../data/site";

const courseIcons = {
  "cpr-aed-first-aid": HeartPulse,
  bls: Activity,
};

export default function Courses() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden">
      <Navbar />

      {/* HEADER */}
      <section className="pt-32 lg:pt-44 pb-14 lg:pb-20" data-testid="courses-header">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-teal">
              Our Programs
            </span>
          </Reveal>
          <h1 className="mt-5 font-editorial font-black text-navy tracking-tight leading-[0.98] text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            <RevealHeading
              lines={["CPR and Emergency", "Training Courses"]}
              delay={0.1}
              testId="courses-heading"
            />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-7 max-w-2xl font-body text-lg leading-relaxed text-slate-600">
              Choose the course that fits your individual, professional, or
              organizational training needs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COURSE DETAILS */}
      <section className="pb-10" data-testid="course-details">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 space-y-6">
          {COURSES.map((c, i) => {
            const Icon = courseIcons[c.id];
            const reversed = i % 2 === 1;
            return (
              <Reveal key={c.id}>
                <article
                  id={c.id}
                  className="scroll-mt-28 grid lg:grid-cols-12 gap-8 rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 lg:p-10 ambient-shadow"
                  data-testid={`course-detail-${c.id}`}
                >
                  <div className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
                    <div className="relative overflow-hidden rounded-3xl aspect-[4/3] bg-slate-200 h-full">
                      <img
                        src={c.image}
                        alt={c.title}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>
                  </div>

                  <div className={`lg:col-span-7 flex flex-col ${reversed ? "lg:order-1" : ""}`}>
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center h-12 w-12 rounded-2xl bg-red-accent/10 text-red-accent">
                        <Icon className="h-6 w-6" strokeWidth={1.75} />
                      </span>
                      <span className="font-grotesk text-xs uppercase tracking-[0.2em] text-teal">
                        Course 0{i + 1}
                      </span>
                    </div>

                    <h2 className="mt-5 font-editorial font-black text-navy tracking-tight text-3xl sm:text-4xl">
                      {c.title}
                    </h2>
                    <p className="mt-4 font-body text-slate-600 leading-relaxed text-lg">
                      {c.short}
                    </p>

                    <div className="mt-6 grid sm:grid-cols-2 gap-6">
                      <div className="rounded-2xl bg-[#D1FAE5]/60 border border-emerald-100 p-5">
                        <div className="flex items-center gap-2 text-navy font-grotesk font-bold">
                          <Users className="h-5 w-5 text-teal" />
                          Who it&apos;s for
                        </div>
                        <p className="mt-2 font-body text-[15px] text-slate-600 leading-relaxed">
                          {c.for}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
                        <div className="font-grotesk font-bold text-navy">
                          What you&apos;ll learn
                        </div>
                        <ul className="mt-3 space-y-2">
                          {c.learn.map((l) => (
                            <li key={l} className="flex gap-2 font-body text-[15px] text-slate-600">
                              <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                              <span>{l}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <Link
                      to="#contact"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById("contact")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="mt-7 self-start inline-flex items-center gap-2 rounded-full bg-red-accent px-7 py-3.5 text-[15px] font-semibold text-white transition-all duration-300 hover:bg-[#be123c] hover:-translate-y-0.5 ambient-shadow"
                      data-testid={`course-request-${c.id}`}
                    >
                      {c.cta}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PROGRAMS */}
      <Programs />

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24 py-20 lg:py-28" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-teal">
                  Contact
                </span>
                <h2 className="mt-4 font-editorial font-black text-navy tracking-tight leading-[1.02] text-4xl sm:text-5xl">
                  Schedule Your Training
                </h2>
                <p className="mt-6 font-body text-lg text-slate-600 leading-relaxed">
                  Tell us which course you are interested in, how many people
                  need training, and your preferred date. A member of Second
                  Chance CPR will contact you by email.
                </p>

                <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#D1FAE5] border border-emerald-200 p-4" data-testid="reach-out-note">
                  <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                  <p className="font-body text-[15px] text-navy leading-relaxed">
                    Once you submit the form, someone from Second Chance CPR will
                    reach out to you by email to confirm your training.
                  </p>
                </div>

                <div className="mt-8 space-y-4">
                  <a
                    href={`mailto:${EMAIL}`}
                    className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 ambient-shadow hover:-translate-y-0.5 transition-transform"
                    data-testid="contact-email-link"
                  >
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-red-accent text-white">
                      <Mail className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-grotesk text-xs uppercase tracking-widest text-slate-400">
                        Email
                      </p>
                      <p className="font-body font-semibold text-navy break-all">
                        {EMAIL}
                      </p>
                    </div>
                  </a>
                  <a
                    href={`tel:${PHONE}`}
                    className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 ambient-shadow hover:-translate-y-0.5 transition-transform"
                    data-testid="contact-phone-link"
                  >
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-red-accent text-white">
                      <Phone className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-grotesk text-xs uppercase tracking-widest text-slate-400">
                        Phone
                      </p>
                      <p className="font-body font-semibold text-navy">
                        {PHONE_DISPLAY}
                      </p>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 ambient-shadow">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-teal text-white">
                      <MapPin className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-grotesk text-xs uppercase tracking-widest text-slate-400">
                        Service Area
                      </p>
                      <p className="font-body font-semibold text-navy">
                        {SERVICE_AREA}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <div className="rounded-[2rem] bg-white border border-slate-200 p-3 sm:p-5 ambient-shadow-lg">
                  <div className="flex items-start gap-3 rounded-2xl bg-[#D1FAE5]/60 border border-emerald-100 px-5 py-4 mb-4">
                    <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                    <p className="font-body text-[15px] text-navy leading-relaxed">
                      Fill out the form below and{" "}
                      <span className="font-semibold">someone will reach out to you</span>{" "}
                      by email to confirm your training.
                    </p>
                  </div>
                  <div className="overflow-hidden rounded-2xl">
                    <iframe
                      title="Second Chance CPR Training Request Form"
                      src={JOTFORM_URL}
                      className="w-full"
                      style={{ minHeight: "900px", border: "none" }}
                      scrolling="yes"
                      data-testid="jotform-embed"
                    />
                  </div>
                  <p className="px-3 pb-3 pt-1 text-center font-body text-sm text-slate-400">
                    Form not loading? Email us directly at{" "}
                    <a href={`mailto:${EMAIL}`} className="text-teal font-medium">
                      {EMAIL}
                    </a>
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
